/**
 * Slide-deck embedding for LMS lessons. One place that turns a stored
 * {provider, embed_url} into an iframe src, shared by the editor preview and
 * both course players so Canva / Google Slides / Office / PDF all render the
 * same way everywhere.
 *
 * - canva:         https://www.canva.com/design/<id>/<token>/view?embed
 * - google_slides: the published /embed URL, iframed directly
 * - office:        rendered through Microsoft's Office web viewer
 * - pdf:           a PDF URL, iframed directly (browsers render PDFs inline)
 */
export type SlidesProvider = 'canva' | 'google_slides' | 'office' | 'pdf';

/** Normalize any Canva share/edit link to its embeddable /view?embed form. */
export function canvaEmbedUrl(url: string): string {
  if (!url) return url;
  const base = url.split('?')[0].replace(/\/(edit|view)\/?$/, '');
  return `${base}/view?embed`;
}

/** The iframe src for a slides lesson, given its provider + stored URL. */
export function slidesEmbedSrc(provider: string | undefined, rawUrl: string): string {
  if (!rawUrl) return '';
  const p = (provider || 'google_slides') as SlidesProvider;
  if (p === 'canva') return canvaEmbedUrl(rawUrl);
  if (p === 'office') return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(rawUrl)}`;
  // google_slides published embed + pdf are iframed directly
  return rawUrl;
}

export const SLIDES_PROVIDERS: { value: SlidesProvider; label: string }[] = [
  { value: 'canva', label: 'Canva' },
  { value: 'google_slides', label: 'Google Slides' },
  { value: 'office', label: 'PowerPoint / PDF (Office viewer)' },
];
